<template>
    <div class="pb-20">
        <div class="container">
            <SectionIntro
                :title="category.translations[0].name"
                :subtitle="
                    category.translations[0].description ||
                    $t('products.category.default_subtitle', { name: category.translations[0].name })
                "
                class="mt-14 lg:mt-20"
                xl
            />
            <button
                v-show="$device.isMobile && category.translations[0].name !== 'Whispers of Wood'"
                class="whitespace-nowrap flex items-center justify-center w-full font-Inter-Bold border border-black bg-white dark:bg-grey-800 py-2 md:py-1 px-5 rounded-full transition text-black dark:text-white lg:hidden mb-6 ml-auto text-sm md:text-base"
                @click="showFilters()"
            >
                <i class="text-xlm mr-2" :class="isFiltersVisible ? 'ri-close-line' : 'ri-filter-3-line'"></i
                ><span>{{ $t('products.filters.show') }}</span>
            </button>
            <transition name="fade">
                <div
                    v-show="
                        (isFiltersVisible || $device.isDesktop) && category.translations[0].name !== 'Whispers of Wood'
                    "
                    class="grid grid-cols-12 mt-8 lg:gap-x-8 gap-y-4 lg:gap-y-0 pb-6 border-opacity-10 mb-4"
                >
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.searchText"
                            :label="$t('products.filters.search_label')"
                            :placeholder="$t('products.filters.search_placeholder')"
                            @input="updateQueries"
                        />
                    </div>

                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.orientation"
                            :label="$t('products.filters.orientation_label')"
                            :placeholder="$t('products.filters.orientation_placeholder')"
                            is-dropdown
                            :options="orientation"
                            @input="updateQueries"
                        />
                    </div>
                    <div class="col-span-12 lg:col-span-4">
                        <Input
                            v-model="filters.arrangement"
                            :label="$t('products.filters.sort_label')"
                            :placeholder="$t('products.filters.sort_placeholder')"
                            is-dropdown
                            :options="arrangement"
                            @input="updateQueries"
                        />
                    </div>
                </div>
            </transition>
            <ProductGrid :products="products" :product-count="10" />
            <ScrollToTopButton />
        </div>
    </div>
</template>

<script>
import { debounce } from 'vue-debounce';
import ProductGrid from '@/components/Layout/ProductGrid.vue';
import ScrollToTopButton from '@/components/UI/Buttons/ScrollToTopButton.vue';
export default {
    name: 'PictureCategory',
    components: {
        ScrollToTopButton,
        ProductGrid,
    },
    async asyncData({ $axios, app, params, query, error }) {
        let products = [];
        let category = [];

        const queryParams = {
            search: query.search,
            orientation: query.orientation,
            order: query.order,
        };

        try {
            await Promise.allSettled([$axios.$get(`/category/${params.category}`, { params: queryParams })])
                .then(([categoryRes]) => {
                    if (categoryRes.status === 'fulfilled' && categoryRes.value.result === 'success') {
                        category = categoryRes.value.data;
                        products = categoryRes.value.data.products;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        if (!category) {
            return error({ statusCode: 404, message: app.i18n.t('products.category.not_available') });
        }

        return { products, category };
    },
    data() {
        return {
            categories: null,
            isFiltersVisible: false,
            filters: {
                searchText: '',
                brand: null,
                orientation: null,
                arrangement: null,
            },
        };
    },
    head() {
        const meta = [
            {
                hid: 'og:title',
                property: 'og:title',
                content: this.category.translations[0].meta_title || this.category.translations[0].name,
            },
            {
                hid: 'title',
                name: 'title',
                content: this.category.translations[0].meta_title || this.category.translations[0].name,
            },
            {
                hid: 'og:description',
                property: 'og:description',
                content: this.category.translations[0].meta_description || this.category.translations[0].description,
            },
            {
                hid: 'description',
                name: 'description',
                content: this.category.translations[0].meta_description || this.category.translations[0].description,
            },
        ];

        if (this.model?.image) {
            meta.push({
                hid: 'og:image',
                property: 'og:image',
                content: this.category?.ogimage || this.category?.image,
            });
            meta.push({
                hid: 'og:image:url',
                property: 'og:image:url',
                content: this.category?.ogimage || this.category?.image,
            });
        }
        return {
            title: this.category.translations[0].meta_title || this.category.translations[0].name,
            meta,
        };
    },
    computed: {
        arrangement() {
            return [
                { id: 'SORT_ASC', name: this.$t('products.filters.sort_oldest') },
                { id: 'SORT_DESC', name: this.$t('products.filters.sort_newest') },
            ];
        },
        orientation() {
            return [
                { id: 'vertical', name: this.$t('products.filters.orientation_vertical') },
                { id: 'horizontal', name: this.$t('products.filters.orientation_horizontal') },
            ];
        },
    },
    watch: {
        $route(from, to) {
            this.filterProducts(from.query, from.params);
        },
    },

    beforeMount() {
        this.filters.searchText = this.$route.query.search;

        if (this.$route.query.orientation) {
            this.filters.orientation = this.$route.query.orientation;
        }

        if (this.$route.query.order) {
            this.filters.arrangement = this.$route.query.order;
        }
    },
    methods: {
        updateQueries: debounce(function () {
            this.$router.push({
                path: this.$route.path,
                query: {
                    search: this.filters.searchText,
                    orientation: this.filters.orientation,
                    order: this.filters.arrangement,
                },
            });
        }, 250),
        filterProducts(query, params) {
            const queryParams = {
                search: query.search,
                orientation: query.orientation,
                order: query.order,
            };

            this.$axios
                .get(`/category/${params.category}`, { params: queryParams })
                .then((response) => {
                    this.products = response.data.data.products;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        showFilters() {
            this.isFiltersVisible = !this.isFiltersVisible;
        },
    },
};
</script>
